import React, { useState, useRef, useCallback, useEffect } from 'react';
import JSZip from 'jszip';
import {
  Upload, X, Sparkles, Play, Archive, RotateCcw, ArrowLeft, Check, AlertTriangle, Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FLOWERS = 25;
const MAX_SIZE_MB = 12;

// Preset prompts — tuned for visual CONSISTENCY across a mixed batch
const PRESETS = [
  {
    id: 'watercolor-sticker',
    label: 'Watercolor sticker',
    prompt:
      'Transform this flower into a soft watercolor illustration sticker. Centered subject on a pure white background. Loose painterly edges, pastel palette, gentle ink outline, single subject only, no shadows, no text, square composition.',
  },
  {
    id: 'heritage-botanical',
    label: 'Heritage botanical',
    prompt:
      'Transform this flower into a vintage botanical illustration in the style of a 19th-century heritage seed catalogue. Detailed fine line work, muted natural colors on a cream background, centered subject, single specimen, no text, no signature, square composition.',
  },
  {
    id: 'flat-vector',
    label: 'Flat vector',
    prompt:
      'Transform this flower into a clean flat vector illustration. Bold outlines, limited palette of 5-6 flat colors, no gradients, no shadows, centered subject on a white background, square composition.',
  },
  {
    id: 'risograph',
    label: 'Risograph zine',
    prompt:
      'Transform this flower into a 2-color risograph print: deep forest green and warm amber on cream paper. Visible halftone texture, slight misregistration, centered subject, square composition, no text.',
  },
  {
    id: 'line-art',
    label: 'Inked line art',
    prompt:
      'Transform this flower into a clean black ink line drawing on pure white. Confident continuous lines, no shading, no fill, centered subject, square composition, no text.',
  },
];

// Pure helpers
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const out = reader.result;
      const idx = out.indexOf(',');
      resolve(idx >= 0 ? out.slice(idx + 1) : out);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
function base64ToBlob(b64, mime = 'image/png') {
  const bin = atob(b64);
  const len = bin.length;
  const buf = new Uint8Array(len);
  for (let i = 0; i < len; i++) buf[i] = bin.charCodeAt(i);
  return new Blob([buf], { type: mime });
}
function safeName(s) {
  return (s || '').trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_') || 'flower';
}

// Client-side bg removal — sample 4 corners, knock out matching pixels with
// a soft feather. Keeps the AI render but produces a transparent-bg PNG.
function knockoutBg(img, tolerance = 36) {
  const w = img.naturalWidth, h = img.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  const id = ctx.getImageData(0, 0, w, h);
  const d = id.data;
  // Sample 4 corner blocks
  function sample(x, y) {
    let r = 0, g = 0, b = 0, n = 0;
    const s = Math.max(4, Math.min(w, h) >> 6);
    for (let yy = y; yy < y + s; yy++) {
      for (let xx = x; xx < x + s; xx++) {
        const i = (yy * w + xx) * 4;
        r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
      }
    }
    return { r: r / n, g: g / n, b: b / n };
  }
  const cs = [
    sample(0, 0),
    sample(w - 6, 0),
    sample(0, h - 6),
    sample(w - 6, h - 6),
  ];
  const tolSum = tolerance * 3;
  const feather = tolSum + 60;
  for (let i = 0; i < d.length; i += 4) {
    let min = Infinity;
    for (const bg of cs) {
      const dist = Math.abs(d[i] - bg.r) + Math.abs(d[i + 1] - bg.g) + Math.abs(d[i + 2] - bg.b);
      if (dist < min) min = dist;
    }
    if (min <= tolSum) d[i + 3] = 0;
    else if (min < feather) d[i + 3] = Math.round(d[i + 3] * ((min - tolSum) / (feather - tolSum)));
  }
  ctx.putImageData(id, 0, 0);
  // Trim transparent borders for tighter PNG
  return trim(c);
}
function trim(canvas) {
  const ctx = canvas.getContext('2d');
  const { width: w, height: h } = canvas;
  const d = ctx.getImageData(0, 0, w, h).data;
  let top = h, bot = 0, l = w, r = 0, found = false;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (d[(y * w + x) * 4 + 3] > 8) {
      found = true;
      if (y < top) top = y; if (y > bot) bot = y;
      if (x < l) l = x; if (x > r) r = x;
    }
  }
  if (!found) return canvas;
  const nw = r - l + 1, nh = bot - top + 1;
  if (nw === w && nh === h) return canvas;
  const out = document.createElement('canvas');
  out.width = nw; out.height = nh;
  out.getContext('2d').drawImage(canvas, l, top, nw, nh, 0, 0, nw, nh);
  return out;
}

export default function FlowerLab({ onExit }) {
  const [flowers, setFlowers] = useState([]); // {id, file, name, previewUrl, status, error, resultBlob, resultUrl, autoName}
  const [prompt, setPrompt] = useState(PRESETS[0].prompt);
  const [presetId, setPresetId] = useState(PRESETS[0].id);
  const [batchName, setBatchName] = useState('flower-batch');
  const [startNum, setStartNum] = useState(1);
  const [pad, setPad] = useState(2);
  const [bgRemove, setBgRemove] = useState(true);
  const [tolerance, setTolerance] = useState(36);
  const [running, setRunning] = useState(false);
  const [cancelToken, setCancelToken] = useState({ cancelled: false });
  const fileRef = useRef(null);

  useEffect(() => () => {
    flowers.forEach(f => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      if (f.resultUrl) URL.revokeObjectURL(f.resultUrl);
    });
  }, [flowers]);

  const computedName = useCallback((idx) => {
    const total = startNum + flowers.length - 1;
    const minPad = Math.max(pad, String(total).length);
    return `${safeName(batchName)}_${String(startNum + idx).padStart(minPad, '0')}`;
  }, [batchName, startNum, pad, flowers.length]);

  function pickPreset(id) {
    setPresetId(id);
    const p = PRESETS.find(x => x.id === id);
    if (p) setPrompt(p.prompt);
  }

  function addFiles(filesIn) {
    const incoming = Array.from(filesIn || []).filter(f =>
      f && f.type && f.type.startsWith('image/') && f.size <= MAX_SIZE_MB * 1024 * 1024
    );
    if (incoming.length === 0) return;
    setFlowers(prev => {
      const room = MAX_FLOWERS - prev.length;
      const allow = incoming.slice(0, room);
      const next = allow.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        name: file.name.replace(/\.[^.]+$/, ''),
        previewUrl: URL.createObjectURL(file),
        status: 'queued', // queued | rendering | done | error
        error: '',
        resultBlob: null,
        resultUrl: null,
      }));
      return [...prev, ...next];
    });
  }

  function removeFlower(id) {
    setFlowers(prev => {
      const f = prev.find(x => x.id === id);
      if (f) {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
        if (f.resultUrl) URL.revokeObjectURL(f.resultUrl);
      }
      return prev.filter(x => x.id !== id);
    });
  }

  function clearAll() {
    if (!window.confirm('Remove all flowers from the lab?')) return;
    flowers.forEach(f => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      if (f.resultUrl) URL.revokeObjectURL(f.resultUrl);
    });
    setFlowers([]);
  }

  // ---- Render one flower ----
  async function renderOne(flower, idx) {
    const b64 = await fileToBase64(flower.file);
    const resp = await fetch(`${BACKEND_URL}/api/lab/render`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_base64: b64, prompt }),
    });
    if (!resp.ok) {
      const errBody = await resp.text().catch(() => '');
      throw new Error(`HTTP ${resp.status}: ${errBody.slice(0, 200)}`);
    }
    const data = await resp.json();
    // Decode the AI image
    const rawBlob = base64ToBlob(data.image_base64, data.mime_type || 'image/png');
    const rawUrl = URL.createObjectURL(rawBlob);
    let finalBlob = rawBlob;
    if (bgRemove) {
      // Load + bg-knockout + trim → transparent PNG
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = rawUrl;
      });
      const canvas = knockoutBg(img, tolerance);
      finalBlob = await new Promise(res => canvas.toBlob(b => res(b), 'image/png'));
      URL.revokeObjectURL(rawUrl);
    }
    return finalBlob;
  }

  async function startBatch() {
    if (running) return;
    if (flowers.length === 0) { window.alert('Add some flowers first.'); return; }
    if (!prompt.trim()) { window.alert('Set a render prompt first.'); return; }
    const token = { cancelled: false };
    setCancelToken(token);
    setRunning(true);

    // Reset all to queued (or keep done ones?) — keep done ones, re-run failed/queued
    setFlowers(prev => prev.map(f => f.status === 'done' ? f : ({ ...f, status: 'queued', error: '' })));

    for (let i = 0; i < flowers.length; i++) {
      if (token.cancelled) break;
      // Read the *latest* flower state inside the loop
      const current = (await new Promise(resolve => {
        setFlowers(prev => { resolve(prev[i]); return prev; });
      }));
      if (!current) continue;
      if (current.status === 'done') continue;

      setFlowers(prev => prev.map(f => f.id === current.id ? ({ ...f, status: 'rendering', error: '' }) : f));
      try {
        const blob = await renderOne(current, i);
        const url = URL.createObjectURL(blob);
        setFlowers(prev => prev.map(f => f.id === current.id ? ({
          ...f,
          status: 'done',
          resultBlob: blob,
          resultUrl: url,
        }) : f));
      } catch (e) {
        const msg = (e && e.message) || String(e);
        setFlowers(prev => prev.map(f => f.id === current.id ? ({
          ...f,
          status: 'error',
          error: msg,
        }) : f));
      }
    }

    setRunning(false);
  }

  function cancelBatch() {
    setCancelToken(t => ({ ...t, cancelled: true }));
  }

  async function exportZip() {
    const done = flowers.filter(f => f.status === 'done' && f.resultBlob);
    if (done.length === 0) { window.alert('No completed renders to export yet.'); return; }
    const zip = new JSZip();
    const folder = zip.folder(safeName(batchName));
    done.forEach((f, idx) => {
      const finalIdx = flowers.findIndex(x => x.id === f.id);
      const name = computedName(finalIdx === -1 ? idx : finalIdx);
      folder.file(`${name}.png`, f.resultBlob);
    });
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName(batchName)}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  const doneCount = flowers.filter(f => f.status === 'done').length;
  const errCount = flowers.filter(f => f.status === 'error').length;
  const totalCount = flowers.length;

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--cream)', color: 'var(--ink)' }} data-testid="flower-lab">
      <header className="px-5 md:px-10 py-6 border-b-2 flex items-center gap-4 flex-wrap" style={{ borderColor: 'var(--ink)' }}>
        <button onClick={onExit} className="inline-flex items-center gap-2 font-mono text-xs tracking-widest" data-testid="lab-exit">
          <ArrowLeft size={16} /> BACK TO REX
        </button>
        <div className="flex-1" />
        <div className="flex items-center gap-2 font-display text-lg md:text-2xl tracking-wider">
          <Sparkles size={22} style={{ color: 'var(--amber)' }} />
          FLOWER LAB
        </div>
        <span className="font-mono text-[10px] tracking-widest opacity-60 ml-2">PRIVATE · OWNER ONLY</span>
      </header>

      <main className="px-5 md:px-10 py-8 max-w-[1200px] mx-auto">
        {/* STEP A — RENDER RECIPE */}
        <section className="mb-8" data-testid="lab-recipe">
          <h3 className="font-display text-xl tracking-wider mb-4">01 · THE RECIPE</h3>
          <div className="border-2 p-4 md:p-5" style={{ borderColor: 'var(--ink)', background: 'var(--cream-2)' }}>
            <p className="font-mono text-[10px] tracking-widest mb-2 opacity-70">STYLE PRESET</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {PRESETS.map(p => (
                <button
                  key={p.id}
                  className={`chip ${presetId === p.id ? 'on' : ''}`}
                  onClick={() => pickPreset(p.id)}
                  data-testid={`lab-preset-${p.id}`}
                >{p.label}</button>
              ))}
            </div>
            <p className="font-mono text-[10px] tracking-widest mb-2 opacity-70">PROMPT (applied identically to every image)</p>
            <textarea
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); setPresetId('custom'); }}
              rows={4}
              className="input-rex w-full font-mono text-xs"
              data-testid="lab-prompt"
            />
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <label className="block">
                <span className="font-mono text-[10px] tracking-widest opacity-70">BATCH NAME</span>
                <input value={batchName} onChange={(e) => setBatchName(e.target.value)} className="input-rex" data-testid="lab-batch-name" />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-widest opacity-70">START NUMBER</span>
                <input type="number" min="0" value={startNum} onChange={(e) => setStartNum(Math.max(0, parseInt(e.target.value || '0', 10)))} className="input-rex" data-testid="lab-start-num" />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-widest opacity-70">DIGIT PADDING</span>
                <input type="number" min="1" max="5" value={pad} onChange={(e) => setPad(Math.max(1, Math.min(5, parseInt(e.target.value || '1', 10))))} className="input-rex" data-testid="lab-pad" />
              </label>
            </div>
            <button type="button" className="check-row mt-4" onClick={() => setBgRemove(v => !v)} data-testid="lab-bgremove">
              <span className={`check-box ${bgRemove ? 'on' : ''}`}>{bgRemove ? '✓' : ''}</span>
              <span className="font-mono text-[11px] tracking-widest">Knock out background → transparent PNG</span>
            </button>
            {bgRemove && (
              <label className="block mt-2 ml-7 max-w-xs">
                <span className="font-mono text-[10px] opacity-60">TOLERANCE: {tolerance}</span>
                <input type="range" min="10" max="80" value={tolerance} onChange={(e) => setTolerance(parseInt(e.target.value, 10))} className="w-full" data-testid="lab-tolerance" />
              </label>
            )}
          </div>
        </section>

        {/* STEP B — FLOWERS */}
        <section className="mb-8" data-testid="lab-flowers-section">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h3 className="font-display text-xl tracking-wider">02 · THE FLOWERS ({totalCount}/{MAX_FLOWERS})</h3>
            {flowers.length > 0 && (
              <button className="font-mono text-[10px] tracking-widest opacity-70 underline" onClick={clearAll} data-testid="lab-clear">CLEAR ALL</button>
            )}
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={running || flowers.length >= MAX_FLOWERS}
            className="dropzone p-8 w-full text-left"
            data-testid="lab-dropzone"
          >
            <div className="flex items-center gap-3">
              <Upload size={20} />
              <div>
                <p className="font-display tracking-widest">DROP UP TO {MAX_FLOWERS} FLOWER PHOTOS</p>
                <p className="font-mono text-[11px] opacity-60">PNG / JPG / WEBP · ≤ {MAX_SIZE_MB}MB EACH</p>
              </div>
            </div>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            data-testid="lab-file-input"
          />

          {flowers.length > 0 && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="lab-grid">
              {flowers.map((f, idx) => (
                <div key={f.id} className="border-2 relative" style={{ borderColor: 'var(--ink)', background: '#fff' }}>
                  <div className="aspect-square flex items-center justify-center overflow-hidden" style={{ backgroundImage: 'linear-gradient(45deg,#e8dfc9 25%,transparent 25%),linear-gradient(-45deg,#e8dfc9 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e8dfc9 75%),linear-gradient(-45deg,transparent 75%,#e8dfc9 75%)', backgroundSize: '14px 14px', backgroundPosition: '0 0,0 7px,7px -7px,-7px 0' }}>
                    {f.resultUrl ? (
                      <img src={f.resultUrl} alt={f.name} className="max-w-full max-h-full object-contain" />
                    ) : (
                      <img src={f.previewUrl} alt={f.name} className="max-w-full max-h-full object-contain" style={{ opacity: f.status === 'rendering' ? 0.4 : 1 }} />
                    )}
                  </div>
                  <div className="p-2 border-t-2" style={{ borderColor: 'var(--ink)' }}>
                    <p className="font-mono text-[10px] truncate">{computedName(idx)}.png</p>
                    <p className="font-mono text-[9px] tracking-widest opacity-60 mt-1 flex items-center gap-1">
                      {f.status === 'queued' && 'QUEUED'}
                      {f.status === 'rendering' && (<><span className="inline-block w-2 h-2 rounded-full" style={{ background: 'var(--amber)' }} /> RENDERING…</>)}
                      {f.status === 'done' && (<><Check size={10} /> DONE</>)}
                      {f.status === 'error' && (<><AlertTriangle size={10} style={{ color: '#b91c1c' }} /> <span style={{ color: '#b91c1c' }} title={f.error}>FAILED</span></>)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFlower(f.id)}
                    className="absolute top-1 right-1 p-1"
                    style={{ background: 'var(--cream)', border: '1px solid var(--ink)' }}
                    title="Remove"
                    data-testid={`lab-remove-${f.id}`}
                    disabled={running}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* STEP C — RENDER + EXPORT */}
        <section className="mb-12" data-testid="lab-actions">
          <h3 className="font-display text-xl tracking-wider mb-4">03 · RUN THE BATCH</h3>
          <div className="border-2 p-5" style={{ borderColor: 'var(--ink)', background: 'var(--ink)', color: 'var(--cream)' }}>
            <p className="font-mono text-xs tracking-widest mb-4" style={{ color: 'var(--cream-2)' }}>
              {totalCount} TOTAL · {doneCount} DONE · {errCount} FAILED
            </p>
            <div className="flex flex-wrap gap-3">
              {!running ? (
                <button
                  className="btn-amber inline-flex items-center gap-2"
                  disabled={flowers.length === 0}
                  onClick={startBatch}
                  data-testid="lab-start"
                >
                  <Play size={16} /> {doneCount > 0 ? 'RESUME / RENDER REMAINING' : 'START RENDERING'}
                </button>
              ) : (
                <button
                  className="inline-flex items-center gap-2 px-4 py-2 font-bold tracking-widest"
                  style={{ background: 'var(--cream)', color: 'var(--ink)' }}
                  onClick={cancelBatch}
                  data-testid="lab-cancel"
                >
                  <RotateCcw size={16} /> STOP AFTER CURRENT
                </button>
              )}
              <button
                className="inline-flex items-center gap-2 px-4 py-2 font-bold tracking-widest"
                style={{ background: 'var(--amber)', color: 'var(--ink)' }}
                onClick={exportZip}
                disabled={doneCount === 0}
                data-testid="lab-export-zip"
              >
                <Archive size={16} /> EXPORT ZIP ({doneCount})
              </button>
              {flowers.some(f => f.status === 'error') && (
                <button
                  className="inline-flex items-center gap-2 px-4 py-2 font-bold tracking-widest"
                  style={{ border: '2px solid var(--cream)', color: 'var(--cream)' }}
                  onClick={() => setFlowers(prev => prev.map(f => f.status === 'error' ? ({ ...f, status: 'queued', error: '' }) : f))}
                  disabled={running}
                  data-testid="lab-retry-failed"
                >
                  <Trash2 size={16} /> RESET FAILED ({errCount})
                </button>
              )}
            </div>
            <p className="font-mono text-[10px] tracking-widest opacity-60 mt-4">
              TIP · IF THE BATCH IS INTERRUPTED, RE-CLICK START — ONLY THE UNFINISHED FLOWERS WILL RE-RENDER.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
